import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, LifeBuoy, Plus, List, LayoutDashboard, ChevronRight, Clock, CheckCircle2,
    AlertCircle, MessageSquare, Send, Paperclip, Search, Filter, ArrowLeft,
    User, Calendar, Tag, Building2, Hash, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use direct API path for helpdesk endpoints (not proxy)
const API_BASE = '/api';

const HelpTicketModal = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, create, list
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Get user info from localStorage
    const authUser = JSON.parse(localStorage.getItem('authUser') || '[{}]')[0] || {};
    const userId = authUser?.userId || authUser?.id || 0;
    const companyId = authUser?.companyId || 0;
    const userName = `${authUser?.firstName || ''} ${authUser?.lastName || ''}`.trim() || 'User';

    // Form state
    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        priority: 'medium',
        category: 'general',
        attachments: []
    });

    useEffect(() => {
        if (isOpen) {
            fetchTickets();
        }
    }, [isOpen]);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/helpdesk/tickets`);
            setTickets(response.data.tickets || []);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            // Use sample data for demo
            setTickets([
                {
                    id: 'TKT-001',
                    subject: 'Unable to generate HBL document',
                    description: 'When I try to print the HBL, the system shows an error.',
                    priority: 'high',
                    category: 'technical',
                    status: 'open',
                    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
                    userId: userId,
                    companyId: companyId,
                    userName: userName,
                    messages: [
                        { sender: 'user', message: 'When I try to print the HBL, the system shows an error.', timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
                        { sender: 'support', message: 'Thank you for reporting this. We are investigating the issue.', timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString() }
                    ]
                },
                {
                    id: 'TKT-002',
                    subject: 'Request for new feature - Bulk Export',
                    description: 'We need the ability to export multiple bookings at once.',
                    priority: 'medium',
                    category: 'feature_request',
                    status: 'in_progress',
                    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                    userId: userId,
                    companyId: companyId,
                    userName: userName,
                    messages: []
                },
                {
                    id: 'TKT-003',
                    subject: 'Dashboard loading slowly',
                    description: 'The dashboard takes too long to load metrics.',
                    priority: 'low',
                    category: 'performance',
                    status: 'resolved',
                    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
                    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                    userId: userId,
                    companyId: companyId,
                    userName: userName,
                    messages: []
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.subject.trim() || !formData.description.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            const ticketData = {
                ...formData,
                userId: userId,
                companyId: companyId,
                userName: userName,
                status: 'open',
                createdAt: new Date().toISOString()
            };

            const response = await axios.post(`${API_BASE}/helpdesk/tickets`, ticketData);
            
            toast.success('Ticket created successfully! Support team has been notified.');
            setFormData({
                subject: '',
                description: '',
                priority: 'medium',
                category: 'general',
                attachments: []
            });
            setActiveTab('list');
            fetchTickets();
        } catch (error) {
            console.error('Error creating ticket:', error);
            // Demo success for UI
            const newTicket = {
                id: `TKT-${String(tickets.length + 1).padStart(3, '0')}`,
                ...formData,
                userId: userId,
                companyId: companyId,
                userName: userName,
                status: 'open',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                messages: []
            };
            setTickets(prev => [newTicket, ...prev]);
            toast.success('Ticket created successfully! Support team has been notified.');
            setFormData({
                subject: '',
                description: '',
                priority: 'medium',
                category: 'general',
                attachments: []
            });
            setActiveTab('list');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'in_progress': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
            case 'resolved': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
            case 'closed': return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
            default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            case 'medium': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
            case 'low': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
            default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
        }
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterStatus === 'all' || ticket.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const stats = {
        total: tickets.length,
        open: tickets.filter(t => t.status === 'open').length,
        inProgress: tickets.filter(t => t.status === 'in_progress').length,
        resolved: tickets.filter(t => t.status === 'resolved').length
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const inputClass = "w-full px-3 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all";
    const selectClass = `${inputClass} appearance-none cursor-pointer`;

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                <LifeBuoy className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">Help Desk</h2>
                                <p className="text-cyan-100 text-xs">Create and manage support tickets</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-slate-200 dark:border-slate-700 px-4">
                        <div className="flex gap-1">
                            {[
                                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                                { id: 'create', label: 'Create Ticket', icon: Plus },
                                { id: 'list', label: 'My Tickets', icon: List }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => { setActiveTab(tab.id); setSelectedTicket(null); }}
                                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                        activeTab === tab.id
                                            ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
                        {/* Dashboard Tab */}
                        {activeTab === 'dashboard' && (
                            <div className="space-y-4">
                                {/* Stats Cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <Hash className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                                                <p className="text-xs text-slate-500">Total Tickets</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                                                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.open}</p>
                                                <p className="text-xs text-slate-500">Open</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                                                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.inProgress}</p>
                                                <p className="text-xs text-slate-500">In Progress</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                                                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.resolved}</p>
                                                <p className="text-xs text-slate-500">Resolved</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-4">
                                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Quick Actions</h3>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setActiveTab('create')}
                                            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors"
                                        >
                                            <Plus className="w-4 h-4" /> New Ticket
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('list')}
                                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            <List className="w-4 h-4" /> View All Tickets
                                        </button>
                                    </div>
                                </div>

                                {/* Recent Tickets */}
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Recent Tickets</h3>
                                    <div className="space-y-2">
                                        {tickets.slice(0, 3).map(ticket => (
                                            <div
                                                key={ticket.id}
                                                onClick={() => { setSelectedTicket(ticket); setActiveTab('list'); }}
                                                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs font-mono text-slate-500">{ticket.id}</span>
                                                    <span className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1">{ticket.subject}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                                        {ticket.status.replace('_', ' ')}
                                                    </span>
                                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Create Ticket Tab */}
                        {activeTab === 'create' && (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                                            Subject <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            placeholder="Brief description of your issue"
                                            className={inputClass}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                                            Category
                                        </label>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            className={selectClass}
                                        >
                                            <option value="general">General Inquiry</option>
                                            <option value="technical">Technical Issue</option>
                                            <option value="billing">Billing</option>
                                            <option value="feature_request">Feature Request</option>
                                            <option value="performance">Performance</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                                            Priority
                                        </label>
                                        <select
                                            name="priority"
                                            value={formData.priority}
                                            onChange={handleInputChange}
                                            className={selectClass}
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                                            Description <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Please provide detailed information about your issue..."
                                            rows={5}
                                            className={`${inputClass} resize-none`}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* User Info Display */}
                                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Ticket will be created with:</p>
                                    <div className="flex flex-wrap gap-4 text-xs">
                                        <div className="flex items-center gap-1.5">
                                            <User className="w-3.5 h-3.5 text-slate-400" />
                                            <span className="text-slate-600 dark:text-slate-300">{userName}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                                            <span className="text-slate-600 dark:text-slate-300">Company ID: {companyId}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Hash className="w-3.5 h-3.5 text-slate-400" />
                                            <span className="text-slate-600 dark:text-slate-300">User ID: {userId}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('dashboard')}
                                        className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        <Send className="w-4 h-4" />
                                        {loading ? 'Creating...' : 'Submit Ticket'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* List Tab */}
                        {activeTab === 'list' && !selectedTicket && (
                            <div className="space-y-4">
                                {/* Search and Filter */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search tickets..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className={`${inputClass} pl-10`}
                                        />
                                    </div>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className={`${selectClass} w-full sm:w-40`}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="open">Open</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="resolved">Resolved</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>

                                {/* Tickets List */}
                                <div className="space-y-2">
                                    {filteredTickets.length === 0 ? (
                                        <div className="text-center py-8">
                                            <LifeBuoy className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                                            <p className="text-slate-500 dark:text-slate-400">No tickets found</p>
                                            <button
                                                onClick={() => setActiveTab('create')}
                                                className="mt-3 text-cyan-500 hover:text-cyan-600 text-sm font-medium"
                                            >
                                                Create your first ticket
                                            </button>
                                        </div>
                                    ) : (
                                        filteredTickets.map(ticket => (
                                            <div
                                                key={ticket.id}
                                                onClick={() => setSelectedTicket(ticket)}
                                                className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-xs font-mono text-slate-500">{ticket.id}</span>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                                                                {ticket.priority}
                                                            </span>
                                                        </div>
                                                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 line-clamp-1">{ticket.subject}</h4>
                                                        <p className="text-xs text-slate-500 line-clamp-1 mt-1">{ticket.description}</p>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                                            {ticket.status.replace('_', ' ')}
                                                        </span>
                                                        <span className="text-xs text-slate-400">{formatDate(ticket.createdAt)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Ticket Detail View */}
                        {activeTab === 'list' && selectedTicket && (
                            <div className="space-y-4">
                                <button
                                    onClick={() => setSelectedTicket(null)}
                                    className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Back to list
                                </button>

                                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                                    <div className="flex items-start justify-between gap-3 mb-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-mono text-slate-500">{selectedTicket.id}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                                                    {selectedTicket.priority}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                                                    {selectedTicket.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">{selectedTicket.subject}</h3>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center gap-1.5">
                                            <User className="w-3.5 h-3.5" />
                                            <span>{selectedTicket.userName}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Calendar className="w-3.5 h-3.5" />
                                            <span>Created: {formatDate(selectedTicket.createdAt)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Tag className="w-3.5 h-3.5" />
                                            <span>{selectedTicket.category.replace('_', ' ')}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Building2 className="w-3.5 h-3.5" />
                                            <span>Company: {selectedTicket.companyId}</span>
                                        </div>
                                    </div>

                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                        <p className="text-slate-600 dark:text-slate-400">{selectedTicket.description}</p>
                                    </div>
                                </div>

                                {/* Messages */}
                                {selectedTicket.messages && selectedTicket.messages.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Conversation</h4>
                                        <div className="space-y-3">
                                            {selectedTicket.messages.map((msg, index) => (
                                                <div
                                                    key={index}
                                                    className={`p-3 rounded-lg ${
                                                        msg.sender === 'user'
                                                            ? 'bg-cyan-50 dark:bg-cyan-900/20 ml-8'
                                                            : 'bg-slate-50 dark:bg-slate-800 mr-8'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                                                            {msg.sender === 'user' ? 'You' : 'Support'}
                                                        </span>
                                                        <span className="text-xs text-slate-400">{formatDate(msg.timestamp)}</span>
                                                    </div>
                                                    <p className="text-sm text-slate-600 dark:text-slate-400">{msg.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default HelpTicketModal;
